import { CenterFlexRow } from "styles/layout";
import { numberToStringWithSign } from "Utility";
import { RoundTimeRows } from "../RoundTimeRows";
import {
  TrackNameHeader,
  CupModeMatchTimesTable,
  CupModeTrackTimesContainer,
  RoundNumberCell,
  TeamNameCell,
  RoundDiffSpan,
  RoundStartScoreSpan,
  TeamScoreCell,
  RoundScoreCell,
  StickyHeader,
  StickyCell,
} from "./styles";
import { CupModeTrack } from "model/Match/Set/CupMode/Track/CupModeTrack";
import { RoundsTrack } from "model/Match/Set/Rounds/Track/RoundsTrack";
import { CupModeRound } from "model/Match/Set/CupMode/Track/CupModeRound";
import { CupModeRoundResult } from "model/Match/Set/CupMode/Track/CupModeRoundResult";
import { RoundsRound } from "model/Match/Set/Rounds/Track/RoundsRound";
import { RoundsRoundResult } from "model/Match/Set/Rounds/Track/RoundsRoundResult";
import { selectRosterDisplayName } from "selectors/Roster";
import { getMatchSetDisplayScores } from "model/Match/Set/MatchSet";
import { ForwardCupModeSet } from "model/Match/Set/CupMode/ForwardCupModeSet";
import { RoundsSet } from "model/Match/Set/Rounds/RoundsSet";
import { ReverseCupModeSet } from "model/Match/Set/CupMode/ReverseCupModeSet";
import { ForwardCupModeLongSet } from "model/Match/Set/CupMode/ForwardCupModeLongSet";
import { KnockoutSet } from "model/Match/Set/Knockout/KnockoutSet";
import { KnockoutTrack } from "model/Match/Set/Knockout/Track/KnockoutTrack";
import { MatchTrack } from "model/Match/Track/MatchTrack";
import {
  getKnockoutLivesLostDisplay,
  KnockoutRoundResult,
} from "model/Match/Set/Knockout/Track/KnockoutRoundResult";
import { KnockoutRound } from "model/Match/Set/Knockout/Track/KnockoutRound";
import { MatchTeams } from "model/Match/MatchTeams";
import { getRosterIdOrPlaceholder } from "model/Roster/Roster";
import { RoundsKnockoutSet } from "model/Match/Set/RoundsKnockout/RoundsKnockoutSet";
import { selectTrackNameFromTournamentId } from "selectors/Tournament";
import { useAppSelector } from "app/hooks";

interface Props {
  set:
    | ForwardCupModeSet
    | ForwardCupModeLongSet
    | KnockoutSet
    | ReverseCupModeSet
    | RoundsSet
    | RoundsKnockoutSet;
  teams: MatchTeams;
  tournamentId: string;
}

export const RoundBasedTrackTimes = ({ set, teams, tournamentId }: Props) => {
  const allRounds = set.tracks.flatMap(
    (track) => track.rounds as (CupModeRound | KnockoutRound | RoundsRound)[]
  );
  const borderIdxs: number[] = [];
  set.tracks.forEach((t) => {
    const span = t.rounds.length;
    const lastIdx = borderIdxs.at(-1) ?? 0;
    borderIdxs.push(lastIdx + span);
  });

  const teamScores = getMatchSetDisplayScores(set);
  return (
    <CupModeTrackTimesContainer>
      <CupModeMatchTimesTable>
        <thead>
          <tr>
            <StickyHeader colSpan={2} />
            {set.tracks.map((track, i) => (
              <RoundBasedMatchTimesTrackHeader
                key={i}
                track={track}
                trackNum={
                  !!track.trackId
                    ? getTrackNum(track.trackId, i, set.tracks)
                    : undefined
                }
                tournamentId={tournamentId}
              />
            ))}
          </tr>
          <tr>
            <StickyCell colSpan={2} />
            {allRounds.map((_, i) => (
              <RoundNumberCell
                key={i}
                alternateBackground={i % 2 === 1}
                colSpan={2}
                leftBorder={borderIdxs.includes(i)}
              >
                Round {i + 1}
              </RoundNumberCell>
            ))}
          </tr>
        </thead>
        <tbody>
          {teams.rosters.map((roster, i) => {
            const rosterId = getRosterIdOrPlaceholder(roster);
            return (
              <RoundBasedMatchTimesTeamRow
                key={`${i}-${rosterId}`}
                rosterId={rosterId}
                tournamentId={tournamentId}
                scoreString={
                  (!!teamScores
                    ? teamScores[rosterId]?.toString()
                    : undefined) ?? "-"
                }
                rounds={allRounds}
                setType={set.type}
                pointsLimit={
                  set.type === "ForwardCupMode" ||
                  set.type === "ForwardCupModeLong"
                    ? set.format.pointsLimit
                    : undefined
                }
                borderIdxs={borderIdxs}
              />
            );
          })}
          <RoundTimeRows
            roundTimes={allRounds.map((r) => r.times)}
            tournamentId={tournamentId}
            showPosition={true}
            padding={1}
            borderIdxs={borderIdxs}
            alternateBackground={true}
            frozen={true}
          />
        </tbody>
      </CupModeMatchTimesTable>
    </CupModeTrackTimesContainer>
  );
};

interface TrackHeaderProps {
  track: CupModeTrack | KnockoutTrack | RoundsTrack;
  trackNum: number | undefined;
  tournamentId: string;
}

const getKoTeamResultCellContents = (result: KnockoutRoundResult) => {
  return (
    <CenterFlexRow>
      <RoundStartScoreSpan>{result.livesAtStart}</RoundStartScoreSpan>
      <RoundDiffSpan>({getKnockoutLivesLostDisplay(result)})</RoundDiffSpan>
    </CenterFlexRow>
  );
};

const getTeamResultCellContents = (
  result: CupModeRoundResult | RoundsRoundResult,
  setType:
    | "ForwardCupMode"
    | "ForwardCupModeLong"
    | "ReverseCupMode"
    | "RoundsKnockout"
    | "Rounds",
  pointsLimit?: number
) => {
  var pointString = result.pointsAtStart.toString();
  var pointTitle: string | undefined;
  if (setType === "ReverseCupMode" && result.pointsAtStart === 0) {
    pointString = "LC";
    pointTitle = "Last Chance";
  } else if (
    (setType === "ForwardCupMode" || setType === "ForwardCupModeLong") &&
    result.pointsAtStart === pointsLimit
  ) {
    pointString = "FI";
    pointTitle = "Finalist";
  }

  return (
    <CenterFlexRow>
      <RoundStartScoreSpan title={pointTitle}>
        {pointString}
      </RoundStartScoreSpan>
      <RoundDiffSpan>
        ({numberToStringWithSign(result.pointsDelta, false)})
      </RoundDiffSpan>
    </CenterFlexRow>
  );
};

const RoundBasedMatchTimesTrackHeader = ({
  track,
  trackNum,
  tournamentId,
}: TrackHeaderProps) => {
  const trackName = useAppSelector((state) =>
    !!track.trackId
      ? selectTrackNameFromTournamentId(state, track.trackId, tournamentId)
      : undefined
  );

  return (
    <TrackNameHeader colSpan={track.rounds.length * 2}>
      {trackName}
      {(trackNum ?? 0) > 1 ? `(${trackNum})` : ""}
    </TrackNameHeader>
  );
};

interface TeamRowProps {
  rosterId: string;
  tournamentId: string;
  scoreString: string;
  rounds: (CupModeRound | KnockoutRound | RoundsRound)[];
  setType:
    | "ForwardCupMode"
    | "ForwardCupModeLong"
    | "Knockout"
    | "ReverseCupMode"
    | "Rounds"
    | "RoundsKnockout";
  pointsLimit: number | undefined;
  borderIdxs: number[];
}

const RoundBasedMatchTimesTeamRow = ({
  rosterId,
  tournamentId,
  scoreString,
  rounds,
  setType,
  pointsLimit,
  borderIdxs,
}: TeamRowProps) => {
  const displayName = useAppSelector((state) =>
    selectRosterDisplayName(state, rosterId, tournamentId)
  );

  return (
    <tr>
      <TeamNameCell>{displayName ?? ""}</TeamNameCell>
      <TeamScoreCell>{scoreString}</TeamScoreCell>
      {rounds.map((round, i) => {
        const rosterResult: any = (
          round.results as (
            | KnockoutRoundResult
            | CupModeRoundResult
            | RoundsRoundResult
          )[]
        ).find((result) => result.rosterId === rosterId);
        return (
          <RoundScoreCell
            key={`${i} ${rosterResult?.pointsAtStart ?? "u"} ${
              rosterResult?.pointsDelta ?? "u"
            }`}
            alternateBackground={i % 2 === 1}
            colSpan={2}
            leftBorder={borderIdxs.includes(i)}
          >
            {!!rosterResult
              ? setType === "Knockout"
                ? getKoTeamResultCellContents(
                    rosterResult as KnockoutRoundResult
                  )
                : getTeamResultCellContents(rosterResult, setType, pointsLimit)
              : ""}
          </RoundScoreCell>
        );
      })}
    </tr>
  );
};

const getTrackNum = (trackId: string, index: number, tracks: MatchTrack[]) => {
  var trackNum = 1;
  for (var i = 0; i < index; i++) {
    if (tracks[i].trackId === trackId) {
      trackNum++;
    }
  }

  return trackNum;
};
